import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase-config';
import '../../assets/styles/AdminDashboard.css';

const AdminDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const navigate = useNavigate();

  const getProjectName = (project) =>
    project.projectName || project.name || project.businessName || project.clientName || 'Untitled Project';

  const getClientName = (project) =>
    project.clientName || project.client || project.businessName || '—';

  const getProjectStatus = (project) => {
    const status = typeof project.status === 'string' ? project.status.trim() : '';
    const normalized = status.toLowerCase();
    const knownStatuses = ['active', 'pending', 'complete', 'completed', 'paused', 'archived'];

    if (knownStatuses.includes(normalized)) return status;
    if (project.finalPaymentMade) return 'Complete';
    if (typeof project.currentStage === 'number') return 'Active';
    return 'Pending';
  };

  const getStatusClass = (project) =>
    getProjectStatus(project).toLowerCase().replace(/\s+/g, '-');

  const getUserName = (user) =>
    [user.firstName, user.lastName].filter(Boolean).join(' ') || user.name || user.email || 'Unnamed Client';

  useEffect(() => {
    const fetchProjectsAndUsers = async () => {
      try {
        const projectSnap = await getDocs(collection(db, 'projects'));
        setProjects(projectSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const usersQuery = query(collection(db, 'users'), where('isAdmin', '==', false));
        const usersSnap = await getDocs(usersQuery);
        setUsers(usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        setLoading(false);
      } catch (err) {
        setError('Failed to load data: ' + err.message);
        setLoading(false);
      }
    };
    fetchProjectsAndUsers();
  }, []);

  const filteredProjects = projects.filter(p =>
    getProjectName(p).toLowerCase().includes(projectSearch.toLowerCase())
  );

  const filteredUsers = users.filter(u =>
    `${getUserName(u)} ${u.email || ''}`.toLowerCase().includes(userSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="admin-loading">
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-loading">
        <p className="admin-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">

      {/* Page header */}
      <div className="admin-dashboard-header">
        <div>
          <p className="admin-dashboard-eyebrow">Admin</p>
          <h1 className="admin-dashboard-title">Dashboard</h1>
        </div>
        <button className="admin-dashboard-primary" onClick={() => navigate('/admin/new-project')}>
          + New Project
        </button>
      </div>

      {/* Stat cards */}
      <div className="dashboard-stats">
        <div className="dashboard-stat-card">
          <span className="dashboard-stat-number">{projects.length}</span>
          <span className="dashboard-stat-label">Total Projects</span>
        </div>
        <div className="dashboard-stat-card">
          <span className="dashboard-stat-number">{users.length}</span>
          <span className="dashboard-stat-label">Total Clients</span>
        </div>
        <div className="dashboard-stat-card">
          <span className="dashboard-stat-number">
            {projects.filter(p => getProjectStatus(p).toLowerCase() === 'active').length}
          </span>
          <span className="dashboard-stat-label">Active Projects</span>
        </div>
      </div>

      {/* Projects */}
      <div className="admin-panel">
        <div className="admin-panel-header">
          <h2 className="admin-panel-title">Projects</h2>
          <input
            type="text"
            className="admin-search"
            placeholder="Search projects..."
            value={projectSearch}
            onChange={(e) => setProjectSearch(e.target.value)}
          />
        </div>

        {filteredProjects.length > 0 ? (
          <div className="admin-table">
            <div className="admin-table-head admin-table-head--projects">
              <span>Project Name</span>
              <span>Status</span>
              <span>Client</span>
              <span></span>
            </div>
            {filteredProjects.map(project => (
              <div key={project.id} className="admin-table-row admin-table-row--projects">
                <span className="admin-row-name">{getProjectName(project)}</span>
                <span>
                  <span className={`admin-badge admin-badge--${getStatusClass(project)}`}>
                    {getProjectStatus(project)}
                  </span>
                </span>
                <span className="admin-row-sub">{getClientName(project)}</span>
                <span>
                  <Link to={`/admin/project/${project.id}`} className="admin-row-link">
                    Open &rarr;
                  </Link>
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="admin-empty">No projects match your search.</p>
        )}
      </div>

      {/* Users */}
      <div className="admin-panel">
        <div className="admin-panel-header">
          <h2 className="admin-panel-title">Clients</h2>
          <input
            type="text"
            className="admin-search"
            placeholder="Search clients..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
          />
        </div>

        {filteredUsers.length > 0 ? (
          <div className="admin-table">
            <div className="admin-table-head admin-table-head--users">
              <span>Name</span>
              <span>Email</span>
              <span></span>
            </div>
            {filteredUsers.map(user => (
              <div key={user.id} className="admin-table-row admin-table-row--users">
                <span className="admin-row-name">{getUserName(user)}</span>
                <span className="admin-row-sub">{user.email || '—'}</span>
                <span>
                  <Link to={`/admin/user/${user.id}`} className="admin-row-link">
                    View &rarr;
                  </Link>
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="admin-empty">No clients match your search.</p>
        )}
      </div>

    </div>
  );
};

export default AdminDashboard;
